import React, { useMemo, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";

import { Button, Modal, Dropdown, Card, Row, Col, Select, Input, Table, Segmented, ConfigProvider } from "antd";
import ScrollDatePicker from "../../components/calendar/ScrollDatePicker";
import esES from "antd/locale/es_ES";
import { HappyProvider } from "@ant-design/happy-work-theme";
import { SearchOutlined } from "@ant-design/icons";
import { DataBadge, AppPagination } from "../../components/ui";
import { usePermisos } from "../../context/PermisosContext";

import "../../components/tribunal/TribunalPage.css";
import "../../components/tribunal/TribunalTablePage.css";
import "./materias/laboral/LaboralProcedimientoPage.css";

import { actionJuntaCards } from "../../redux/actions/junta/junta";
import { actionAutoridadesJuntaGet } from "../../redux/actions/autoridades/autoridades";
import { STATUS_PILL_HEX } from "./materias/laboral/tribunalEstatus.js";
import { printJuntaReportPdf, previewJuntaReportPdf } from "../../components/utils/printJuntaReportPdf.js";
import ImportarExcelModal from "./utils/ImportarExcelModal.jsx";
import useLaboralCatalogos from "./materias/laboral/useLaboralCatalogos.js";
import useDefaultCiudadUsuario from "./materias/laboral/useDefaultCiudadUsuario.js";

dayjs.locale("es");

const ALL = "__all__";
const PAGE_SIZE = 10;

const coerceItems = (slice) => {
  if (!slice) return [];
  const data = slice.data ?? slice;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  return [];
};

const collapseSpaces = (str) => String(str || "").replace(/\s+/g, " ").trim();

const formatDateShortEs = (d) => {
  if (!d) return "N/A";
  const x = dayjs(d);
  if (!x.isValid()) return String(d);
  return x.format("DD MMM YYYY HH:mm [hrs]");
};

function eventoBsColor(kind) {
  const map = { yellow: "warning", blue: "info", green: "success", red: "danger", gray: "secondary" };
  return map[kind] || "secondary";
}

function isEventoEstaSemana(fechaRaw) {
  if (!fechaRaw) return false;
  const d = dayjs(fechaRaw);
  if (!d.isValid()) return false;
  if (d.isBefore(dayjs(), "day")) return false;
  return d.isSame(dayjs(), "week");
}

const pickNextEvent = (item) => {
  if (item?.fecha_audiencia) {
    return {
      eventoFecha: formatDateShortEs(item.fecha_audiencia),
      eventoTipo: "Audiencia",
      eventoKind: "yellow",
      eventoFechaRaw: item.fecha_audiencia,
    };
  }
  if (item?.fecha_notificacion) {
    return {
      eventoFecha: formatDateShortEs(item.fecha_notificacion),
      eventoTipo: "Notificación",
      eventoKind: "blue",
      eventoFechaRaw: item.fecha_notificacion,
    };
  }
  return {
    eventoFecha: "N/A",
    eventoTipo: "Sin fecha próxima",
    eventoKind: "gray",
    eventoFechaRaw: null,
  };
};

const mapJuntaToRow = (item) => {
  const isArchived = item?.active === 0 || item?.active === "0";
  const estatus = isArchived
    ? { label: "ARCHIVADO", kind: "gray" }
    : { label: "ACTIVO", kind: "primary" };

  const actorName = item?.nombre_actora || "—";
  const razonSocial =
    item?.nombre_empresa ||
    (Array.isArray(item?.razones_sociales) && item.razones_sociales.length > 0
      ? item.razones_sociales.map((r) => r.razon_social).join(", ")
      : null);
  const contraName = razonSocial || "—";
  const caseTitle = contraName !== "—" ? `${actorName} vs ${contraName}` : actorName;
  const evento = pickNextEvent(item);

  return {
    id: item?.id_junta,
    expediente: item?.expediente || "—",
    actor: actorName,
    contraparte: contraName,
    caseTitle,
    abogado: item?.abogado ? collapseSpaces(item.abogado) : "Sin abogado registrado",
    estatus,
    autoridad: item?.nombre_autoridad || "—",
    razonSocial: razonSocial || "—",
    ...evento,
  };
};

// ===== Íconos SVG reutilizados de Tribunal/Centro para mantener el mismo
// lenguaje visual (mismos paths, mismo peso, mismos colores) =====
const ICONS = {
  file: (
    <path d="M256 0v128h128L256 0zM224 128L224 0H48C21.49 0 0 21.49 0 48v416C0 490.5 21.49 512 48 512h288c26.51 0 48-21.49 48-48V160h-127.1C238.3 160 224 145.7 224 128zM272 416h-160C103.2 416 96 408.8 96 400C96 391.2 103.2 384 112 384h160c8.836 0 16 7.162 16 16C288 408.8 280.8 416 272 416zM272 352h-160C103.2 352 96 344.8 96 336C96 327.2 103.2 320 112 320h160c8.836 0 16 7.162 16 16C288 344.8 280.8 352 272 352zM288 272C288 280.8 280.8 288 272 288h-160C103.2 288 96 280.8 96 272C96 263.2 103.2 256 112 256h160C280.8 256 288 263.2 288 272z" />
  ),
  gavel: (
    <path d="M172.5 131.1C186.8 117.5 194.6 98.61 194.6 79.53c0-16.75-6.031-33.7-18.22-47.11C163.7 18.59 145.4 10.98 126.1 10.98c-17.7 0-34.6 6.62-47.63 18.66L18.72 89.66C6.031 101.6 0 118 0 134.1c0 17.14 6.531 34.31 19.55 47.13c13.03 12.83 30.11 19.14 47.19 19.14c17.09 0 33.98-6.375 46.72-19.05L172.5 131.1zM528.6 322.2c-13.03-12.83-30.11-19.14-47.19-19.14c-17.09 0-33.98 6.375-46.72 19.05l-59.06 50.93c-14.28 13.55-22.06 32.44-22.06 51.53c0 16.75 6.031 33.7 18.22 47.11c13.03 13.83 31.33 21.44 49.66 21.44c17.7 0 34.6-6.62 47.63-18.66l60.72-60.02c12.69-11.98 18.72-28.39 18.72-44.48C548.6 352.2 542 335 528.6 322.2zM416 128h96V64h-96V0h-64v64h-96v64h96v64h64V128z" />
  ),
  calendar: (
    <path d="M160 32V64H288V32C288 14.33 302.3 0 320 0C337.7 0 352 14.33 352 32V64H400C426.5 64 448 85.49 448 112V160H0V112C0 85.49 21.49 64 48 64H96V32C96 14.33 110.3 0 128 0C145.7 0 160 14.33 160 32zM0 192H448V464C448 490.5 426.5 512 400 512H48C21.49 512 0 490.5 0 464V192zM80 256C71.16 256 64 263.2 64 272V368C64 376.8 71.16 384 80 384H176C184.8 384 192 376.8 192 368V272C192 263.2 184.8 256 176 256H80z" />
  ),
  checkCircle: (
    <path d="M0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256zM371.8 211.8C382.7 200.9 382.7 183.1 371.8 172.2C360.9 161.3 343.1 161.3 332.2 172.2L224 280.4L179.8 236.2C168.9 225.3 151.1 225.3 140.2 236.2C129.3 247.1 129.3 264.9 140.2 275.8L204.2 339.8C215.1 350.7 232.9 350.7 243.8 339.8L371.8 211.8z" />
  ),
  info: (
    <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 128c17.67 0 32 14.33 32 32c0 17.67-14.33 32-32 32S224 177.7 224 160C224 142.3 238.3 128 256 128zM296 384h-80C202.8 384 192 373.3 192 360s10.75-24 24-24h16v-64H224c-13.25 0-24-10.75-24-24S210.8 224 224 224h32c13.25 0 24 10.75 24 24v88h16c13.25 0 24 10.75 24 24S309.3 384 296 384z" />
  ),
  plus: (
    <path d="M432 256c0 17.69-14.33 32.01-32 32.01H256v144c0 17.69-14.33 31.99-32 31.99s-32-14.3-32-31.99v-144H48c-17.67 0-32-14.32-32-32.01s14.33-31.99 32-31.99H192v-144c0-17.69 14.33-32.01 32-32.01s32 14.32 32 32.01v144h144C417.7 224 432 238.3 432 256z" />
  ),
};

function StatusPillJunta({ kind, label }) {
  return (
    <div style={{ width: 130, flexShrink: 0 }}>
      <DataBadge
        color={STATUS_PILL_HEX[kind] || kind}
        className="d-flex align-items-center justify-content-center gap-1 status-pill-badge"
        style={{ whiteSpace: "normal", width: "100%", boxSizing: "border-box", lineHeight: 1.3, textAlign: "center" }}
      >
        {label}
      </DataBadge>
    </div>
  );
}

const KPI_BG_VARIANT = { blue: "info", green: "success", gray: "secondary", yellow: "warning", red: "danger", primary: "primary" };

const KPI_ICON_COLORS = {
  primary: { bg: "#DCE9F5", fg: "#0A539A" },
  success: { bg: "#16A34A26", fg: "#16A34A" },
  secondary: { bg: "#F3F4F6", fg: "#6B7280" },
  warning: { bg: "#FEF3C7", fg: "#F59E0B" },
  danger: { bg: "#FEE2E2", fg: "#DC2626" },
  info: { bg: "#e6f4ff", fg: "#1677ff" },
};

function KpiCardJunta({ iconSvg, iconBg, value, label, active = false, onClick }) {
  const variant = KPI_BG_VARIANT[iconBg] || "secondary";
  const iconColors = KPI_ICON_COLORS[variant] || KPI_ICON_COLORS.secondary;
  return (
    <Card
      role={onClick ? "button" : undefined}
      onClick={onClick}
      bodyStyle={{ display: "flex", alignItems: "center", gap: 12 }}
      style={{
        cursor: onClick ? "pointer" : "default",
        borderColor: active ? "#0A539A" : undefined,
        borderWidth: active ? 2 : undefined,
      }}
    >
      <span
        className="d-inline-flex align-items-center justify-content-center"
        style={{ width: 44, height: 44, background: iconColors.bg, color: iconColors.fg }}
      >
        {iconSvg}
      </span>
      <div>
        <div className="fs-4 fw-bold lh-1">{value}</div>
        <div className="small text-secondary">{label}</div>
      </div>
    </Card>
  );
}

function FooterPagerJunta({ total, page, setPage }) {
  const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);
  return (
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
      <small className="text-secondary">
        Mostrando <strong>{start}</strong> a <strong>{end}</strong> de <strong>{total}</strong> resultados
      </small>
      <AppPagination current={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} size="sm" />
    </div>
  );
}

function TableView({ pageRows, total, page, setPage, navigate, location, canConsultar }) {
  const columns = [
    {
      title: "Expediente / Actor",
      key: "expediente",
      width: 260,
      render: (_, r) => (
        <>
          <div className="fw-semibold">{r.expediente}</div>
          <div className="small fw-semibold text-uppercase">{r.caseTitle}</div>
        </>
      ),
    },
    {
      title: "Abogado & Estado",
      key: "estatus",
      render: (_, r) => (
        <>
          <div className="small mb-1">{r.abogado}</div>
          <StatusPillJunta kind={r.estatus.kind} label={r.estatus.label} />
        </>
      ),
    },
    {
      title: "Autoridad",
      key: "autoridad",
      render: (_, r) => (
        <>
          <div className="small" title={r.autoridad}>{r.autoridad}</div>
          <div className="small text-secondary">
            <i className="bi bi-building me-1" />
            {r.razonSocial}
          </div>
        </>
      ),
    },
    {
      title: "Próximo evento",
      key: "evento",
      render: (_, r) => (
        <div
          className={`exp-audiencia-highlight exp-audiencia-highlight--${eventoBsColor(r.eventoKind)}${
            isEventoEstaSemana(r.eventoFechaRaw) ? " exp-audiencia-highlight--soon" : ""
          }`}
        >
          <div className="exp-audiencia-highlight-icon-wrap">
            <svg className="exp-audiencia-highlight-icon" viewBox="0 0 448 512" width="17" height="17" fill="currentColor" aria-hidden="true">
              {ICONS.calendar}
            </svg>
          </div>
          <div className="exp-audiencia-highlight-body">
            <div className="exp-audiencia-highlight-label">Próximo evento</div>
            <div className="exp-audiencia-highlight-value">{r.eventoFecha}</div>
            <div className="exp-audiencia-highlight-sub">{r.eventoTipo}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Acciones",
      key: "acciones",
      align: "right",
      render: (_, r) => (
        <Button
          type="primary"
          block
          onClick={() => canConsultar && navigate(`/materias/laboral/junta-conciliacion/${r.id}${location.search}`)}
          disabled={!canConsultar}
          title={!canConsultar ? "Sin permiso de consulta" : undefined}
        >
          <svg className="me-1" viewBox="0 0 512 512" width="13" height="13" fill="currentColor" aria-hidden="true" style={{ verticalAlign: "-1px" }}>
            {ICONS.info}
          </svg>
          Ver detalles
        </Button>
      ),
    },
  ];

  return (
    <Card bodyStyle={{ padding: 0 }}>
      <Table columns={columns} dataSource={pageRows} rowKey={(r) => `${r.expediente}-${r.id}`} pagination={false} />
      <div className="p-3" style={{ borderTop: "1px solid #eef2f7", background: "#fff" }}>
        <FooterPagerJunta total={total} page={page} setPage={setPage} />
      </div>
    </Card>
  );
}

function CardsView({ rows, total, page, setPage, navigate, location, canConsultar }) {
  return (
    <>
      <Row gutter={[24, 24]}>
        {rows.map((r) => (
          <Col key={`${r.expediente}-${r.id}`} xs={24} md={12} xl={8} xxl={6}>
            <Card className="h-100 exp-card" bodyStyle={{ display: "flex", flexDirection: "column", gap: 8, height: "100%" }}>
              <div className="d-flex align-items-start justify-content-between gap-2">
                <div className="fw-semibold text-uppercase">{r.caseTitle}</div>
                <StatusPillJunta kind={r.estatus.kind} label={r.estatus.label} />
              </div>

              <div className="d-flex align-items-start gap-2 small">
                <i className="bi bi-person-fill mt-1" />
                <div>
                  <div className="text-secondary text-uppercase" style={{ fontSize: 11 }}>
                    Abogado
                  </div>
                  <div>{r.abogado}</div>
                </div>
              </div>

              <hr className="my-1" />

              <div className="d-flex flex-column gap-1 small text-secondary">
                <div>
                  <svg className="me-1" viewBox="0 0 640 512" width="14" height="14" fill="currentColor" aria-hidden="true" style={{ verticalAlign: "-1px" }}>
                    {ICONS.gavel}
                  </svg>
                  {r.autoridad}
                </div>
                <div>
                  <i className="bi bi-building me-1" />
                  {r.razonSocial}
                </div>
                <div>
                  <i className="bi bi-hash me-1" />
                  Expediente: <strong>{r.expediente}</strong>
                </div>
              </div>

              <div className="mt-auto">
                <div
                  className={`exp-audiencia-highlight exp-audiencia-highlight--${eventoBsColor(r.eventoKind)} mb-2${
                    isEventoEstaSemana(r.eventoFechaRaw) ? " exp-audiencia-highlight--soon" : ""
                  }`}
                >
                  <div className="exp-audiencia-highlight-icon-wrap">
                    <svg className="exp-audiencia-highlight-icon" viewBox="0 0 448 512" width="17" height="17" fill="currentColor" aria-hidden="true">
                      {ICONS.calendar}
                    </svg>
                  </div>
                  <div className="exp-audiencia-highlight-body">
                    <div className="exp-audiencia-highlight-label">Próximo evento</div>
                    <div className="exp-audiencia-highlight-value">{r.eventoFecha}</div>
                    <div className="exp-audiencia-highlight-sub">{r.eventoTipo}</div>
                  </div>
                </div>

                <Button
                  type="primary"
                  block
                  onClick={() => canConsultar && navigate(`/materias/laboral/junta-conciliacion/${r.id}${location.search}`)}
                  disabled={!canConsultar}
                  title={!canConsultar ? "Sin permiso de consulta" : undefined}
                >
                  <svg className="me-1" viewBox="0 0 512 512" width="13" height="13" fill="currentColor" aria-hidden="true" style={{ verticalAlign: "-1px" }}>
                    {ICONS.info}
                  </svg>
                  Ver detalles
                </Button>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
      <div className="mt-3">
        <FooterPagerJunta total={total} page={page} setPage={setPage} />
      </div>
    </>
  );
}

const EXPORT_MENU_TONES = {
  primary: { bg: "#DCE9F5", fg: "#0A539A" },
  green: { bg: "#DCFCE7", fg: "#16A34A" },
};

function ExportMenuRow({ viewBox, path, tone, title, subtitle }) {
  const colors = EXPORT_MENU_TONES[tone] || EXPORT_MENU_TONES.primary;
  const [, , vbW, vbH] = viewBox.split(" ").map(Number);
  const height = 16;
  const width = Math.round((vbW / vbH) * height);
  return (
    <div className="laboral-export-menu-row">
      <span className="laboral-export-menu-icon" style={{ background: colors.bg, color: colors.fg }}>
        <svg viewBox={viewBox} width={width} height={height} fill="currentColor" aria-hidden="true">
          <path d={path} />
        </svg>
      </span>
      <div className="laboral-export-menu-text">
        <div className="laboral-export-menu-title">{title}</div>
        <div className="laboral-export-menu-subtitle">{subtitle}</div>
      </div>
    </div>
  );
}

const SORT_OPTIONS = [
  { value: "default", label: "Ordenar por" },
  { value: "az", label: "Nombre (A-Z)" },
  { value: "za", label: "Nombre (Z-A)" },
  { value: "audiencia_asc", label: "Fecha de audiencia · más próxima" },
  { value: "audiencia_desc", label: "Fecha de audiencia · más lejana" },
  { value: "notificacion_asc", label: "Fecha de notificación · más próxima" },
  { value: "notificacion_desc", label: "Fecha de notificación · más lejana" },
];

function applySortOption(items, sortOption) {
  const list = [...items];
  const byName = (it) => collapseSpaces(it?.nombre_actora || "").toLowerCase();
  const byDate = (it, field) => {
    const d = dayjs(it?.[field]);
    return d.isValid() ? d.valueOf() : null;
  };
  const compareDates = (a, b, field, dir) => {
    const da = byDate(a, field);
    const db = byDate(b, field);
    if (da === null && db === null) return 0;
    if (da === null) return 1;
    if (db === null) return -1;
    return dir === "asc" ? da - db : db - da;
  };

  switch (sortOption) {
    case "az":
      list.sort((a, b) => byName(a).localeCompare(byName(b)));
      break;
    case "za":
      list.sort((a, b) => byName(b).localeCompare(byName(a)));
      break;
    case "audiencia_asc":
      list.sort((a, b) => compareDates(a, b, "fecha_audiencia", "asc"));
      break;
    case "audiencia_desc":
      list.sort((a, b) => compareDates(a, b, "fecha_audiencia", "desc"));
      break;
    case "notificacion_asc":
      list.sort((a, b) => compareDates(a, b, "fecha_notificacion", "asc"));
      break;
    case "notificacion_desc":
      list.sort((a, b) => compareDates(a, b, "fecha_notificacion", "desc"));
      break;
    default:
      // Sin ordenamiento elegido: siempre caer con los expedientes más
      // recientes primero (por fecha de creación, o id como respaldo).
      list.sort((a, b) => {
        const cmp = compareDates(a, b, "created_at", "desc");
        if (cmp !== 0) return cmp;
        return Number(b?.id || 0) - Number(a?.id || 0);
      });
      break;
  }
  return list;
}

export default function LaboralJuntaConciliacionPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { perm } = usePermisos() || { perm: () => true };
  const canConsultar = perm("junta", "consultar");
  const canInsertar = perm("junta", "insertar");

  const [view, setView] = useState("cards");
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [selectedEstadoId, setSelectedEstadoId] = useState(ALL);
  const [selectedCiudadId, setSelectedCiudadId] = useState(ALL);
  const [selectedAutoridadId, setSelectedAutoridadId] = useState(ALL);
  const [selectedAutoridadData, setSelectedAutoridadData] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [statusFilter, setStatusFilter] = useState("activos");
  const [page, setPage] = useState(1);
  const [sortOption, setSortOption] = useState("default");
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [exportPreviewOpen, setExportPreviewOpen] = useState(false);
  const [exportPreviewHtml, setExportPreviewHtml] = useState("");

  const juntaSlice = useSelector((s) => s.junta || {});
  const autoridadesSlice = useSelector((s) => s.autoridades || {});

  const {
    estadosOptions,
    estadoSeleccionado,
    ciudadSeleccionada,
    ciudadesById,
  } = useLaboralCatalogos(selectedEstadoId, selectedCiudadId, { modulo: "junta" });

  // Ciudad por default según lo asignado al usuario (1 ciudad -> esa; varias
  // -> San Luis Río Colorado si está entre ellas, si no la primera alfabética).
  const { defaultCiudad, loaded: ciudadUsuarioLoaded } = useDefaultCiudadUsuario();
  const defaultCiudadAppliedRef = useRef(false);

  useEffect(() => {
    if (defaultCiudadAppliedRef.current) return;
    if (!ciudadUsuarioLoaded) return;
    if (!defaultCiudad) {
      defaultCiudadAppliedRef.current = true;
      return;
    }
    if (!ciudadesById || Object.keys(ciudadesById).length === 0) return;

    if (selectedEstadoId !== ALL || selectedCiudadId !== ALL) {
      defaultCiudadAppliedRef.current = true;
      return;
    }

    defaultCiudadAppliedRef.current = true;
    const idCiudad = Number(defaultCiudad.id_ciudad);
    if (!Number.isFinite(idCiudad)) return;

    setSelectedCiudadId(idCiudad);
    const city = ciudadesById?.[String(idCiudad)] || ciudadesById?.[idCiudad];
    const idEstado = city?.id_estado ?? city?.idEstado ?? null;
    if (idEstado !== undefined && idEstado !== null && String(idEstado) !== "") {
      setSelectedEstadoId(Number(idEstado));
    }
    setPage(1);
  }, [ciudadUsuarioLoaded, defaultCiudad, ciudadesById, selectedEstadoId, selectedCiudadId]);

  const estadosOptionsFinal = useMemo(() => {
    const base = Array.isArray(estadosOptions) ? estadosOptions : [];
    return [{ label: "Todos los estados", value: ALL }, ...base];
  }, [estadosOptions]);

  const ciudadesOptionsFinal = useMemo(() => {
    // Base: TODAS las ciudades conocidas (no depende de tener un estado elegido)
    const baseAll = Object.values(ciudadesById || {}).map((c) => ({
      label: c.nombre ?? c.code ?? `Ciudad ${c.id}`,
      value: c.id,
    }));

    // Si hay estado elegido, acota a las ciudades de ese estado
    const byEstado =
      selectedEstadoId === ALL
        ? baseAll
        : baseAll.filter((o) => {
            const city = ciudadesById?.[String(o.value)] || ciudadesById?.[Number(o.value)];
            const idEstado = city?.id_estado ?? city?.idEstado ?? null;
            return Number(idEstado) === Number(selectedEstadoId);
          });

    return [{ label: "Todas las ciudades", value: ALL }, ...byEstado];
  }, [ciudadesById, selectedEstadoId]);

  const autoridades = useMemo(() => {
    const d = autoridadesSlice?.data ?? autoridadesSlice;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.items)) return d.items;
    if (Array.isArray(d?.data)) return d.data;
    return [];
  }, [autoridadesSlice]);

  // Autoridad: filtrada en cascada -> si hay ciudad elegida, solo esa ciudad;
  // si solo hay estado elegido (sin ciudad), todas las autoridades de ese estado;
  // si no hay nada elegido, catálogo completo.
  const autoridadOptions = useMemo(() => {
    const base = autoridades
      .filter((au) => {
        if (selectedCiudadId !== ALL) {
          return Number(au?.id_ciudad || 0) === Number(selectedCiudadId);
        }
        if (selectedEstadoId !== ALL) {
          const city = ciudadesById?.[String(au?.id_ciudad)] || ciudadesById?.[Number(au?.id_ciudad)];
          const idEstado = city?.id_estado ?? city?.idEstado ?? null;
          return Number(idEstado) === Number(selectedEstadoId);
        }
        return true;
      })
      .map((au) => ({ label: collapseSpaces(au?.nombre || ""), value: au?.id, data: au }))
      .filter((o) => o.label);

    return [{ label: "Todas las autoridades", value: ALL, data: null }, ...base];
  }, [autoridades, selectedCiudadId, selectedEstadoId, ciudadesById]);

  // si por alguna razón llega null, lo normalizas a ALL
  useEffect(() => {
    if (selectedEstadoId == null) {
      setSelectedEstadoId(ALL);
      setSelectedCiudadId(ALL);
      setSelectedAutoridadId(ALL);
      setSelectedAutoridadData(null);
      setPage(1);
    }
  }, [selectedEstadoId]);

  // si cambias estado y la ciudad ya no es válida, fuerza ALL
  useEffect(() => {
    const base = Array.isArray(ciudadesOptionsFinal) ? ciudadesOptionsFinal : [];
    if (!base.length) return;

    const exists = base.some((o) => String(o.value) === String(selectedCiudadId));
    if (exists) return;

    setSelectedCiudadId(ALL);
    setSelectedAutoridadId(ALL);
    setSelectedAutoridadData(null);
    setPage(1);
  }, [ciudadesOptionsFinal, selectedCiudadId]);

  // autoridad: si la seleccion no existe en la lista, fuerza ALL (no "primera")
  useEffect(() => {
    const disponibles = Array.isArray(autoridadOptions) ? autoridadOptions : [];
    if (!disponibles.length) return;

    const exists = disponibles.some((o) => String(o.value) === String(selectedAutoridadId));
    if (exists) return;

    setSelectedAutoridadId(ALL);
    setSelectedAutoridadData(null);
    setPage(1);
  }, [autoridadOptions, selectedAutoridadId]);

  const handleChangeEstado = (value) => {
    const v = value ?? ALL;
    setSelectedEstadoId(v);
    setSelectedCiudadId(ALL);
    setSelectedAutoridadId(ALL);
    setSelectedAutoridadData(null);
    setPage(1);
  };

  const handleChangeCiudad = (value) => {
    const v = value ?? ALL;
    setSelectedCiudadId(v);
    setSelectedAutoridadId(ALL);
    setSelectedAutoridadData(null);
    setPage(1);
  };

  const lastKeyRef = useRef("");

  const filtros = useMemo(
    () => ({
      id_autoridad: selectedAutoridadId !== ALL ? Number(selectedAutoridadId) : undefined,
      id_ciudad: selectedCiudadId !== ALL ? Number(selectedCiudadId) : undefined,
      id_estado: selectedEstadoId !== ALL ? Number(selectedEstadoId) : undefined,
      search: searchDebounced || undefined,
      fecha_inicio: dateRange?.[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : undefined,
      fecha_fin: dateRange?.[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : undefined,
    }),
    [selectedAutoridadId, selectedCiudadId, selectedEstadoId, searchDebounced, dateRange]
  );

  useEffect(() => {
    dispatch(actionAutoridadesJuntaGet());
  }, [dispatch]);

  useEffect(() => {
    const key = JSON.stringify(filtros);
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;
    dispatch(actionJuntaCards(filtros));
  }, [dispatch, filtros]);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const allItems = useMemo(() => coerceItems(juntaSlice), [juntaSlice]);

  const filteredItems = useMemo(() => {
    const hoy = dayjs();
    if (statusFilter === "conAudiencia")
      return allItems.filter((i) => i.fecha_audiencia && dayjs(i.fecha_audiencia).isAfter(hoy));
    if (statusFilter === "sinNotificacion") return allItems.filter((i) => !i.fecha_notificacion);
    if (statusFilter === "activos") return allItems.filter((i) => Number(i.active) === 1);
    if (statusFilter === "archivados") return allItems.filter((i) => Number(i.active) === 0);
    return allItems;
  }, [allItems, statusFilter]);

  const statusCounts = useMemo(() => {
    const hoy = dayjs();
    return {
      proximasAudiencias: allItems.filter(
        (i) =>
          i.fecha_audiencia &&
          dayjs(i.fecha_audiencia).isAfter(hoy) &&
          dayjs(i.fecha_audiencia).diff(hoy, "day") <= 30
      ).length,
      sinNotificacion: allItems.filter((i) => !i.fecha_notificacion).length,
      activos: allItems.filter((i) => Number(i.active) === 1).length,
      archivados: allItems.filter((i) => Number(i.active) === 0).length,
    };
  }, [allItems]);

  const sortedItems = useMemo(() => applySortOption(filteredItems, sortOption), [filteredItems, sortOption]);

  const total = sortedItems.length;
  const paginatedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedItems.slice(start, start + PAGE_SIZE);
  }, [sortedItems, page]);

  const rows = useMemo(() => paginatedItems.map(mapJuntaToRow), [paginatedItems]);

  const handleCrearExpediente = () => navigate("/materias/laboral/junta-conciliacion/crear");

  const selectedAutoridadLabel = useMemo(() => {
    if (selectedAutoridadId === ALL) return "Todas";
    const found = autoridadOptions.find((o) => String(o.value) === String(selectedAutoridadId));
    return found?.label || "Todas";
  }, [selectedAutoridadId, autoridadOptions]);

  const handleExportNow = () => {
    const list = Array.isArray(sortedItems) ? sortedItems : [];
    const now = new Date();
    const y = now.getFullYear();
    const periodFrom = dateRange?.[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : `${y}-01-01`;
    const periodTo = dateRange?.[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : `${y}-12-31`;
    const nombreBusqueda = search || "Todos";
    const html = previewJuntaReportPdf({
      items: list,
      periodFrom,
      periodTo,
      autoridadLabel: selectedAutoridadLabel,
      nombreBusqueda,
    });
    setExportPreviewHtml(html);
    setExportPreviewOpen(true);
  };

  return (
    <ConfigProvider locale={esES}>
      <HappyProvider>
        <main className="trib-main">
          <Modal
            open={exportPreviewOpen}
            onCancel={() => setExportPreviewOpen(false)}
            centered
            width={1140}
            title="Previsualización"
            footer={[
              <Button key="close" onClick={() => setExportPreviewOpen(false)}>
                Cerrar
              </Button>,
              <Button
                key="export"
                type="primary"
                onClick={() => {
                  const list = Array.isArray(sortedItems) ? sortedItems : [];
                  const now = new Date();
                  const y = now.getFullYear();
                  const periodFrom = dateRange?.[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : `${y}-01-01`;
                  const periodTo = dateRange?.[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : `${y}-12-31`;
                  const nombreBusqueda = search || "Todos";
                  printJuntaReportPdf({
                    items: list,
                    periodFrom,
                    periodTo,
                    autoridadLabel: selectedAutoridadLabel,
                    nombreBusqueda,
                  });
                }}
              >
                Exportar PDF
              </Button>,
            ]}
          >
            <div style={{ height: "72vh", border: "1px solid #e5e7eb", overflow: "hidden" }}>
              <iframe
                title="preview-junta"
                style={{ width: "100%", height: "100%", border: 0, background: "#fff" }}
                srcDoc={exportPreviewHtml}
              />
            </div>
          </Modal>
          <div className="trib-wrap">
            {/* PANEL FILTROS */}
            <div className="laboral-filters-panel">
              <div className="laboral-panel-header">
                <h1 className="laboral-panel-title">Junta de Conciliación · Materia laboral</h1>
                {estadoSeleccionado && ciudadSeleccionada ? (
                  <p className="laboral-panel-subtitle">
                    Asuntos ante la Junta de Conciliación y Arbitraje en <b>{estadoSeleccionado.nombre}</b> /{" "}
                    <b>{ciudadSeleccionada.nombre}</b>.
                  </p>
                ) : (
                  <p className="laboral-panel-subtitle">Asuntos ante la Junta de Conciliación y Arbitraje.</p>
                )}
              </div>

              {/* Fila 1: Estado / Ciudad / Autoridad */}
              <div className="laboral-filter-row-1">
                <div className="laboral-field">
                  <div className="laboral-field-label">ESTADO</div>
                  <Select
                    className="laboral-control"
                    value={selectedEstadoId}
                    options={estadosOptionsFinal}
                    onChange={handleChangeEstado}
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </div>

                <div className="laboral-field">
                  <div className="laboral-field-label">CIUDAD</div>
                  <Select
                    className="laboral-control"
                    value={selectedCiudadId}
                    options={ciudadesOptionsFinal}
                    onChange={handleChangeCiudad}
                    disabled={!selectedEstadoId}
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </div>

                <div className="laboral-field">
                  <div className="laboral-field-label">AUTORIDAD</div>
                  <Select
                    className="laboral-control"
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    options={autoridadOptions}
                    value={selectedAutoridadId}
                    onChange={(v, option) => {
                      setSelectedAutoridadId(v ?? ALL);
                      setSelectedAutoridadData(option?.data || null);
                      setPage(1);
                    }}
                  />
                </div>
              </div>

              {/* Fila 2: Buscador (izq, ancho) + Fecha (centro) + Botones (der) */}
              <div className="laboral-filter-row-2">
                <div className="laboral-field laboral-field--search">
                  <div className="laboral-field-label">BUSCADOR</div>
                  <Input
                    className="laboral-search-modern__input"
                    prefix={<SearchOutlined className="laboral-search-modern__icon" />}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Buscar por expediente, actor, abogado..."
                  />
                </div>

                <div className="laboral-field laboral-field--dates">
                  <div className="laboral-field-label">FECHA DE AUDIENCIA</div>
                  <div className="laboral-date-range">
                    <ScrollDatePicker
                      value={dateRange?.[0] ? dayjs(dateRange[0]) : null}
                      format="YYYY-MM-DD"
                      placeholder="Inicio"
                      allowClear
                      onChange={(d) => setDateRange((prev) => [d || null, prev?.[1] ?? null])}
                    />
                    <span className="laboral-date-range-sep">–</span>
                    <ScrollDatePicker
                      value={dateRange?.[1] ? dayjs(dateRange[1]) : null}
                      format="YYYY-MM-DD"
                      placeholder="Fin"
                      allowClear
                      onChange={(d) => setDateRange((prev) => [prev?.[0] ?? null, d || null])}
                    />
                  </div>
                </div>

                <div className="laboral-field laboral-field--actions">
                  <div className="laboral-field-label">&nbsp;</div>
                  <div className="laboral-actions">
                    <Button
                      onClick={() => {
                        setSearch("");
                        setDateRange(null);
                        setSelectedEstadoId(ALL);
                        setSelectedCiudadId(ALL);
                        setSelectedAutoridadId(ALL);
                        setSelectedAutoridadData(null);
                        setPage(1);
                      }}
                      className="laboral-btn-clean"
                    >
                      Limpiar
                    </Button>
                    {canInsertar && (
                      <Button
                        type="primary"
                        onClick={handleCrearExpediente}
                        className="laboral-btn-create"
                        icon={
                          <svg viewBox="0 0 448 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                            {ICONS.plus}
                          </svg>
                        }
                      >
                        Crear
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* KPI cards */}
            {/* marginBottom:-12 replica el espacio que en Tribunal/Centro de
                Conciliación producen los 3 bloques de subfiltros colapsados
                (laboral-substats-wrap) entre las KPI y el listado; Junta no
                tiene subfiltros, así que se ajusta directo para el mismo gap
                visual (26px -> 14px, medido). */}
            <Row gutter={[16, 16]} style={{ marginBottom: -12 }}>
              <Col xs={24} sm={12} md={6}>
                <KpiCardJunta
                  iconBg="yellow"
                  iconSvg={
                    <svg viewBox="0 0 448 512" width="18" height="18" fill="currentColor" aria-hidden="true">
                      {ICONS.calendar}
                    </svg>
                  }
                  value={String(statusCounts.proximasAudiencias)}
                  label="Próximas Audiencias"
                  active={statusFilter === "conAudiencia"}
                  onClick={() => {
                    setStatusFilter((p) => (p === "conAudiencia" ? "todos" : "conAudiencia"));
                    setPage(1);
                  }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <KpiCardJunta
                  iconBg="red"
                  iconSvg={<span className="material-icons-round" style={{ fontSize: 20 }}>notifications_off</span>}
                  value={String(statusCounts.sinNotificacion)}
                  label="Sin Notificación"
                  active={statusFilter === "sinNotificacion"}
                  onClick={() => {
                    setStatusFilter((p) => (p === "sinNotificacion" ? "todos" : "sinNotificacion"));
                    setPage(1);
                  }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <KpiCardJunta
                  iconBg="primary"
                  iconSvg={
                    <svg viewBox="0 0 512 512" width="18" height="18" fill="currentColor" aria-hidden="true">
                      {ICONS.checkCircle}
                    </svg>
                  }
                  value={String(statusCounts.activos)}
                  label="Activos"
                  active={statusFilter === "activos"}
                  onClick={() => {
                    setStatusFilter((p) => (p === "activos" ? "todos" : "activos"));
                    setPage(1);
                  }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <KpiCardJunta
                  iconBg="gray"
                  iconSvg={<span className="material-icons-round" style={{ fontSize: 20 }}>archive</span>}
                  value={String(statusCounts.archivados)}
                  label="Archivados"
                  active={statusFilter === "archivados"}
                  onClick={() => {
                    setStatusFilter((p) => (p === "archivados" ? "todos" : "archivados"));
                    setPage(1);
                  }}
                />
              </Col>
            </Row>

            {/* LISTADO */}
            <div className="viewWrap">
              <div className="viewWrap__header">
                <div className="viewWrap__title">
                  <h2 className="sectionTitle">Expedientes ({total})</h2>
                  <div className="sectionMeta">{total} encontrados</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, flexWrap: "wrap" }}>
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      className: "laboral-export-menu",
                      items: [
                        {
                          key: "export",
                          label: (
                            <ExportMenuRow
                              viewBox="0 0 384 512"
                              path="M88 304H80V256H88C101.3 256 112 266.7 112 280C112 293.3 101.3 304 88 304zM192 256H200C208.8 256 216 263.2 216 272V336C216 344.8 208.8 352 200 352H192V256zM224 0V128C224 145.7 238.3 160 256 160H384V448C384 483.3 355.3 512 320 512H64C28.65 512 0 483.3 0 448V64C0 28.65 28.65 0 64 0H224zM64 224C55.16 224 48 231.2 48 240V368C48 376.8 55.16 384 64 384C72.84 384 80 376.8 80 368V336H88C118.9 336 144 310.9 144 280C144 249.1 118.9 224 88 224H64zM160 368C160 376.8 167.2 384 176 384H200C226.5 384 248 362.5 248 336V272C248 245.5 226.5 224 200 224H176C167.2 224 160 231.2 160 240V368zM288 224C279.2 224 272 231.2 272 240V368C272 376.8 279.2 384 288 384C296.8 384 304 376.8 304 368V320H336C344.8 320 352 312.8 352 304C352 295.2 344.8 288 336 288H304V256H336C344.8 256 352 248.8 352 240C352 231.2 344.8 224 336 224H288zM256 0L384 128H256V0z"
                              tone="primary"
                              title="Exportar PDF"
                              subtitle="Genera un reporte de los expedientes filtrados"
                            />
                          ),
                          disabled: !selectedAutoridadId,
                          onClick: handleExportNow,
                        },
                        { type: "divider" },
                        {
                          key: "importExcel",
                          label: (
                            <ExportMenuRow
                              viewBox="0 0 384 512"
                              path="M224 128L224 0H48C21.49 0 0 21.49 0 48v416C0 490.5 21.49 512 48 512h288c26.51 0 48-21.49 48-48V160h-127.1C238.3 160 224 145.7 224 128zM272.1 264.4L224 344l48.99 79.61C279.6 434.3 271.9 448 259.4 448h-26.43c-5.557 0-10.71-2.883-13.63-7.617L192 396l-27.31 44.38C161.8 445.1 156.6 448 151.1 448H124.6c-12.52 0-20.19-13.73-13.63-24.39L160 344L111 264.4C104.4 253.7 112.1 240 124.6 240h26.43c5.557 0 10.71 2.883 13.63 7.613L192 292l27.31-44.39C222.2 242.9 227.4 240 232.9 240h26.43C271.9 240 279.6 253.7 272.1 264.4zM256 0v128h128L256 0z"
                              tone="green"
                              title="Importar Excel"
                              subtitle="Carga masiva desde una plantilla"
                            />
                          ),
                          onClick: () => setImportModalOpen(true),
                        },
                      ],
                    }}
                  >
                    <Button size="large" className="laboral-btn-create">
                      <svg className="me-1" viewBox="0 0 256 512" width="7" height="13" fill="currentColor" aria-hidden="true" style={{ verticalAlign: "-1px" }}>
                        <path d="M249.6 392.3l-104 112c-9.094 9.781-26.09 9.781-35.19 0l-103.1-112c-6.484-6.984-8.219-17.17-4.406-25.92S14.45 352 24 352H80V160H24C14.45 160 5.812 154.3 1.999 145.6C-1.813 136.8-.0781 126.7 6.406 119.7l104-112c9.094-9.781 26.09-9.781 35.19 0l104 112c6.484 6.984 8.219 17.17 4.406 25.92C250.2 154.3 241.5 160 232 160H176v192h56c9.547 0 18.19 5.656 22 14.41S256.1 385.3 249.6 392.3z" />
                      </svg>
                      Exportar / Importar
                    </Button>
                  </Dropdown>
                  <ImportarExcelModal
                    open={importModalOpen}
                    onClose={() => setImportModalOpen(false)}
                    onSuccess={() => dispatch(actionJuntaCards(filtros))}
                    endpoint="junta-conciliacion/importar-excel"
                    context={{
                      estado: selectedEstadoId !== ALL ? selectedEstadoId : undefined,
                      ciudad: selectedCiudadId !== ALL ? selectedCiudadId : undefined,
                      autoridad: selectedAutoridadId !== ALL ? selectedAutoridadId : undefined,
                    }}
                  />
                  <Select
                    className="laboral-control laboral-sort-control"
                    size="large"
                    style={{ width: 240 }}
                    popupMatchSelectWidth={false}
                    value={sortOption}
                    onChange={(value) => {
                      setSortOption(value || "default");
                      setPage(1);
                    }}
                    options={SORT_OPTIONS}
                  />
                  <Segmented
                  className="laboral-segmented"
                  value={view}
                  onChange={setView}
                  options={[
                    {
                      value: "cards",
                      label: (
                        <span className="laboral-segmented-option">
                          <svg viewBox="0 0 320 512" width="8" height="13" fill="currentColor" aria-hidden="true">
                            <path d="M88 352C110.1 352 128 369.9 128 392V440C128 462.1 110.1 480 88 480H40C17.91 480 0 462.1 0 440V392C0 369.9 17.91 352 40 352H88zM280 352C302.1 352 320 369.9 320 392V440C320 462.1 302.1 480 280 480H232C209.9 480 192 462.1 192 440V392C192 369.9 209.9 352 232 352H280zM40 320C17.91 320 0 302.1 0 280V232C0 209.9 17.91 192 40 192H88C110.1 192 128 209.9 128 232V280C128 302.1 110.1 320 88 320H40zM280 192C302.1 192 320 209.9 320 232V280C320 302.1 302.1 320 280 320H232C209.9 320 192 302.1 192 280V232C192 209.9 209.9 192 232 192H280zM40 160C17.91 160 0 142.1 0 120V72C0 49.91 17.91 32 40 32H88C110.1 32 128 49.91 128 72V120C128 142.1 110.1 160 88 160H40zM280 32C302.1 32 320 49.91 320 72V120C320 142.1 302.1 160 280 160H232C209.9 160 192 142.1 192 120V72C192 49.91 209.9 32 232 32H280z" />
                          </svg>
                          Tarjetas
                        </span>
                      ),
                    },
                    {
                      value: "table",
                      label: (
                        <span className="laboral-segmented-option">
                          <svg viewBox="0 0 512 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                            <path d="M448 32C483.3 32 512 60.65 512 96V416C512 451.3 483.3 480 448 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H448zM224 256V160H64V256H224zM64 320V416H224V320H64zM288 416H448V320H288V416zM448 256V160H288V256H448z" />
                          </svg>
                          Tabla
                        </span>
                      ),
                    },
                  ]}
                  />
                </div>
              </div>

              {view === "table" ? (
                <TableView pageRows={rows} total={total} page={page} setPage={setPage} navigate={navigate} location={location} canConsultar={canConsultar} />
              ) : (
                <CardsView rows={rows} total={total} page={page} setPage={setPage} navigate={navigate} location={location} canConsultar={canConsultar} />
              )}
            </div>
          </div>
        </main>
      </HappyProvider>
    </ConfigProvider>
  );
}
